import Constants from 'expo-constants';

/**
 * ADHA journey markers — same catalogue APIs as before; login JWT added on button press.
 *
 * Flow:
 * 1. POST /v1/uae-pass/login (Basic Auth) → JWT (Bearer for later ADHA APIs)
 * 2. GET /v1/customers/profile-details (Bearer only — no eid/type query)
 * 3. GET /v1/customers/profile (Bearer only — no ID query)
 * 4. GET /v1/units (Bearer only — no project_id) → catalogue list
 *
 * UI / route guards branch on `appStore.isAdhaJourney` (set via setAdhaSession).
 */
export const ADHA_JOURNEY_LOG_PREFIX = 'AdhaJourney';

/** Store field persisted with the session — see appStore.setAdhaSession */
export const ADHA_JOURNEY_FLAG = 'isAdhaJourney' as const;

/** Login endpoint that starts the ADHA catalogue journey (distinct from /v1/uae-pass/callback). */
export const ADHA_UAE_PASS_LOGIN_ENDPOINT = '/v1/uae-pass/login' as const;

function encodeBase64(value: string): string {
  if (typeof btoa !== 'undefined') {
    return btoa(value);
  }
  return Buffer.from(value, 'utf8').toString('base64');
}

function getAdhaConfigExtra(): Record<string, unknown> | undefined {
  const config =
    Constants.expoConfig ??
    (Constants as { manifest2?: { extra?: { expoClient?: unknown } } }).manifest2
      ?.extra?.expoClient;
  return (config as { extra?: Record<string, unknown> } | null | undefined)?.extra;
}

function readAdhaConfigValue(key: string, fallback?: string): string {
  const extra = getAdhaConfigExtra();
  const fromExtra = extra?.[key];
  if (typeof fromExtra === 'string' && fromExtra.length > 0) {
    return fromExtra;
  }
  const fromEnv = process.env[key];
  if (typeof fromEnv === 'string' && fromEnv.length > 0) {
    return fromEnv;
  }
  return fallback ?? '';
}

export function getAdhaBasicCredentials(): { username: string; password: string } {
  const username = readAdhaConfigValue('EXPO_PUBLIC_ADHA_BASIC_USER');
  const password = readAdhaConfigValue('EXPO_PUBLIC_ADHA_BASIC_PASSWORD');

  if (!username || !password) {
    throw new Error(
      'ADHA Basic Auth is not configured. Set EXPO_PUBLIC_ADHA_BASIC_USER and EXPO_PUBLIC_ADHA_BASIC_PASSWORD in .env.',
    );
  }

  return { username, password };
}

/**
 * Axios/request headers with `Authorization: Basic …`.
 * apiClient preserves this and does not overwrite with Bearer.
 */
export function getAdhaBasicAuthHeaders(): { Authorization: string } {
  const { username, password } = getAdhaBasicCredentials();
  const credentials = encodeBase64(`${username}:${password}`);
  return {
    Authorization: `Basic ${credentials}`,
  };
}

/**
 * Purchased / converted lead flag from login APIs.
 * Accepts top-level, `data`, or `details` so Emirates ID verify-otp and
 * UAE Pass `/v1/uae-pass/login` can share one reader.
 */
export function readConvertedLeadFlag(source: unknown): boolean {
  if (!source || typeof source !== 'object') {
    return false;
  }
  const record = source as Record<string, unknown>;
  if (record.convertedLead === true) {
    return true;
  }
  for (const nestKey of ['data', 'details'] as const) {
    const nested = record[nestKey];
    if (
      nested &&
      typeof nested === 'object' &&
      (nested as Record<string, unknown>).convertedLead === true
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Enter ADHA catalogue journey only when the user is not already a converted lead.
 * Emirates ID: also requires a `uaepass` JWT claim (existing gate).
 */
export function shouldEnterAdhaJourney(params: Readonly<{
  convertedLead: boolean;
  hasUaepassClaim?: boolean;
}>): boolean {
  if (params.convertedLead) {
    return false;
  }
  if (params.hasUaepassClaim === undefined) {
    return true;
  }
  return params.hasUaepassClaim === true;
}

/** True when the access token payload includes the ADHA `uaepass` claim. */
export function jwtHasUaepassClaim(token: string): boolean {
  try {
    const segment = token.split('.')[1];
    if (!segment) {
      return false;
    }
    const payload = JSON.parse(atob(segment)) as { uaepass?: unknown };
    return Boolean(payload.uaepass);
  } catch {
    return false;
  }
}
